<template>
  <div class="consumer-home">
    <div class="main-content-box">
      <div class="top-banner-box">
        <swiper
          class="banner-container"
          :space-between="24"
          :loop="true"
          :navigation="true"
          :pagination="{
            clickable: true,
          }"
          :autoplay="{
            delay: 4000,
            disableOnInteraction: false,
          }"
          :modules="[Navigation, Pagination, Autoplay]"
        >
          <swiper-slide
            class="banner-slide"
            v-for="(item, index) in topBannerDatas"
            :key="index"
          >
            <img
              :src="item.mobileUrl"
              @click="handleClickRedirectBanner(item.redirect)"
            />
          </swiper-slide>
        </swiper>
      </div>
      <nav>
        <router-link
          v-for="item in categoryDatas"
          :key="item.id"
          :to="`/list?category=${item.id}`"
          >{{ item.title }}</router-link
        >
      </nav>
    </div>
    <hr />
    <div class="event-products-box">
      <h3>현재 진행중인 <span>이벤트 특가</span> 상품</h3>
      <div class="event-products-container">
        <button
          @click="handleClickProduct(item)"
          v-for="(item, index) in eventProductDatas"
          :key="index"
        >
          <img :src="item.productThumbnailUrl.originalUrl" />
          <p class="name">{{ item.productName }}</p>
          <p class="price" v-if="visiblePriceCode(item, userData) === 0">
            로그인 후 가격 확인
          </p>
          <p class="price" v-else-if="visiblePriceCode(item, userData) === 1">
            {{ item.productBankSellPrice.toLocaleString() }}원 ({{
              item.productSellPrice.toLocaleString()
            }}원)
          </p>
          <p class="price" v-else>오프라인 전용 상품</p>
        </button>
      </div>
    </div>
    <hr />
    <div class="event-products-box">
      <h3>새로 출시된 <span>따끈따끈한</span> 신상품</h3>
      <div class="event-products-container">
        <button
          @click="handleClickProduct(item)"
          v-for="(item, index) in newProductDatas"
          :key="index"
        >
          <img :src="item.productThumbnailUrl.originalUrl" />
          <p class="name">{{ item.productName }}</p>
          <p class="price" v-if="visiblePriceCode(item, userData) === 0">
            로그인 후 가격 확인
          </p>
          <p class="price" v-else-if="visiblePriceCode(item, userData) === 1">
            {{ item.productBankSellPrice.toLocaleString() }}원 ({{
              item.productSellPrice.toLocaleString()
            }}원)
          </p>
          <p class="price" v-else>오프라인 전용 상품</p>
        </button>
      </div>
    </div>
    <hr />
    <div class="popular-products-box">
      <h3>지금 가장 <span>인기 있는</span> 상품</h3>
      <div
        class="products-container"
        v-for="(products, categoryId) in saleDatasByCategory"
        :key="categoryId"
      >
        <div class="title-container">
          <h3>{{ categoryId }}</h3>
        </div>
        <hr />
        <div class="grid-container" v-if="products.length > 0">
          <button
            @click="handleClickProduct(item)"
            v-for="(item, index) in products"
            :key="index"
          >
            <img :src="item.productThumbnailUrl.originalUrl" />
            <p class="name">{{ item.productName }}</p>
            <p class="price" v-if="visiblePriceCode(item, userData) === 0">
              로그인 후 가격 확인
            </p>
            <p class="price" v-else-if="visiblePriceCode(item, userData) === 1">
              {{ item.productBankSellPrice.toLocaleString() }}원 ({{
                item.productSellPrice.toLocaleString()
              }}원)
            </p>
            <p class="price" v-else>오프라인 전용 상품</p>
          </button>
        </div>
        <div class="empty-container" v-else>
          <span class="material-icons-outlined"> error_outline </span>
          <p>추천 상품이 없습니다.</p>
        </div>
      </div>
    </div>
    <NoticePopupDialog
      v-model="isNoticeOpen"
      :variant="isMobilePage ? 'mobile' : 'pc'"
      :popups="popupList"
      @image-click="handleClickPopupImage"
      @hide-today="handleHideTodayPopup"
      @close="handleClosePopup"
    />
  </div>
</template>

<script setup lang="js">
import { onMounted, ref } from "vue";
import { db } from "@/lib/firebase";
import {
  getDocs,
  query,
  collection,
  where,
  orderBy,
  limit,
  getDoc,
  doc,
} from "firebase/firestore";
import { Swiper, SwiperSlide } from "swiper/vue";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import router from "@/router";
import { getUserId } from "@/lib/auth";
import { visiblePriceCode } from "@/lib/utils";
import { useBusyLock } from "@/lib/useBusyLock";

// ✅ 팝업 컴포넌트 (경로는 프로젝트 구조에 맞게 수정)
import NoticePopupDialog from "@/components/admin/NoticePopupDialog.vue";

const { runLocked } = useBusyLock();

const topBannerDatas = ref([]);
const eventProductDatas = ref([]);
const newProductDatas = ref([]);
const saleDatasByCategory = ref({});
const mainNewsDatas = ref([]);
const userData = ref(null);

const saleCategoryIds = ["97", "49", "50", "51", "52", "58", "59", "70", "76", "84"];

/* -------------------------------------------------
 * 공지 팝업 상태
 * ------------------------------------------------- */
// --- PC 페이지면 false, 모바일 페이지면 true로 페이지별 고정값 사용
// --- (모바일 홈 파일에서는 true로 바꿔주면 됨)
const isMobilePage = ref(true);

const isNoticeOpen = ref(false);
const popupList = ref([]);

/* -------------------------------------------------
 * 유틸
 * ------------------------------------------------- */
// --- 공통: docs -> 배열 변환
function mapDocs(snapshot) {
  return snapshot.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));
}

// --- 팝업 오늘 숨김 localStorage key 생성 (팝업별/날짜별)
function getTodayMainPopupHideKey() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `MAIN_POPUP_HIDE_ALL_${y}${m}${day}`;
}

// --- 오늘 숨김 여부 체크
function isMainPopupHiddenToday() {
  return localStorage.getItem(getTodayMainPopupHideKey()) === "Y";
}

/* -------------------------------------------------
 * 클릭 핸들러
 * ------------------------------------------------- */
// --- 배너 클릭 처리 (외부/내부 링크 분기)
const handleClickRedirectBanner = (redirect) => {
  try {
    if (!redirect || typeof redirect !== "string") return;

    if (redirect.startsWith("http://") || redirect.startsWith("https://")) {
      window.open(redirect, "_blank");
      return;
    }

    router.push(redirect);
  } catch (error) {
    console.error(error);
  }
};

// --- 상품 클릭 처리 (노출권한/가격정책 기준)
const handleClickProduct = (item) => {
  switch (visiblePriceCode(item, userData.value)) {
    case 0:
      alert("로그인 후 이용 가능합니다!");
      router.push("/login");
      break;

    case 1:
      router.push(`/product?id=${item.productId}`);
      break;

    case 2:
      alert("오프라인 전용 상품입니다!\n카카오톡이나 전화로 문의해주세요!");
      break;
  }
};

// --- 팝업 이미지 클릭 처리 (redirect 이동)
const handleClickPopupImage = (item) => {
  try {
    const redirect = item?.redirect;
    if (!redirect || typeof redirect !== "string") return;

    if (redirect.startsWith("http://") || redirect.startsWith("https://")) {
      window.open(redirect, "_blank");
      return;
    }

    router.push(redirect);
  } catch (error) {
    console.error("팝업 이동 실패:", error);
  }
};

// --- "오늘은 그만 볼래요"
const handleHideTodayPopup = () => {
  localStorage.setItem(getTodayMainPopupHideKey(), "Y");
  isNoticeOpen.value = false;
};

// --- "팝업 닫기"
const handleClosePopup = () => {
  isNoticeOpen.value = false;
};

/* -------------------------------------------------
 * 데이터 조회 함수들
 * ------------------------------------------------- */
// --- 유저 데이터 조회 (로그인 시만)
async function fetchUserData() {
  const userId = getUserId();
  if (!userId) {
    userData.value = null;
    return;
  }

  const userSnap = await getDoc(doc(db, "users", userId));
  userData.value = userSnap.exists() ? userSnap.data() : null;
}

// --- 상단 배너 조회
async function fetchTopBanners() {
  const snap = await getDocs(
    query(
      collection(db, "banners"),
      where("category", "==", "MAIN_TOP_BANNER"),
      orderBy("order", "asc")
    )
  );
  topBannerDatas.value = mapDocs(snap);
}

// --- 메인 뉴스 조회
async function fetchMainNews() {
  const snap = await getDocs(
    query(collection(db, "mainNews"), orderBy("createdAt", "desc"), limit(2))
  );
  mainNewsDatas.value = mapDocs(snap);
}

// --- 이벤트 상품 조회
async function fetchEventProducts() {
  const snap = await getDocs(
    query(
      collection(db, "product"),
      where("isActive", "==", true),
      where("productCategory", "array-contains", "43"),
      orderBy("popularScore", "desc"),
      orderBy("createdAt", "desc"),
      limit(2)
    )
  );
  eventProductDatas.value = mapDocs(snap);
}

// --- 신상품 조회
async function fetchNewProducts() {
  const snap = await getDocs(
    query(
      collection(db, "product"),
      where("isActive", "==", true),
      orderBy("createdAt", "desc"),
      limit(2)
    )
  );
  newProductDatas.value = mapDocs(snap);
}

// --- 세일 카테고리별 상품 조회 (카테고리명 + 상품목록 병렬 처리)
async function fetchSaleProductsByCategory() {
  // --- 1) 카테고리 문서 병렬 조회
  const categorySnaps = await Promise.all(
    saleCategoryIds.map((categoryId) => getDoc(doc(db, "category", categoryId)))
  );

  // --- 2) 카테고리 메타 정리
  const categoryMetas = categorySnaps
    .map((snap, idx) => {
      if (!snap.exists()) return null;

      const data = snap.data();
      return {
        categoryId: saleCategoryIds[idx],
        categoryName: data?.categoryName || saleCategoryIds[idx],
      };
    })
    .filter(Boolean);

  // --- 3) 각 카테고리 상품 쿼리 병렬 조회
  const saleSnaps = await Promise.all(
    categoryMetas.map((meta) =>
      getDocs(
        query(
          collection(db, "product"),
          where("isActive", "==", true),
          where("productCategory", "array-contains", meta.categoryId),
          orderBy("popularScore", "desc"),
          orderBy("createdAt", "desc"),
          limit(4)
        )
      )
    )
  );

  // --- 4) 카테고리명 기준 객체 구성
  const result = {};
  categoryMetas.forEach((meta, idx) => {
    result[meta.categoryName] = mapDocs(saleSnaps[idx]);
  });

  saleDatasByCategory.value = result;
}

// --- 메인 공지 팝업 조회 (category=MAIN_POPUP 고정)
async function fetchMainPopups() {
  try {
    if (isMainPopupHiddenToday()) {
      popupList.value = [];
      isNoticeOpen.value = false;
      return;
    }

    const snap = await getDocs(
      query(
        collection(db, "banners"),
        where("category", "==", "MAIN_POPUP"),
        orderBy("order", "asc")
      )
    );

    popupList.value = mapDocs(snap);
    isNoticeOpen.value = popupList.value.length > 0;
  } catch (error) {
    console.error("메인 팝업 조회 실패:", error);
    popupList.value = [];
    isNoticeOpen.value = false;
  }
}

/* -------------------------------------------------
 * 페이지 초기화
 * ------------------------------------------------- */
// --- 페이지 초기화 (중복 클릭/중복 진입 방지)
const initializePage = () =>
  runLocked(async () => {
    // --- 서로 독립적인 데이터는 병렬 처리
    await Promise.all([
      fetchUserData(),
      fetchTopBanners(),
      fetchMainNews(),
      fetchEventProducts(),
      fetchNewProducts(),
      fetchMainPopups(), // ✅ 메인 팝업 추가
    ]);

    // --- 카테고리 세일 상품은 쿼리 수가 많아서 분리
    await fetchSaleProductsByCategory();
  }).catch((error) => {
    console.error("Failed to initialize main page:", error);
  });

onMounted(() => {
  initializePage();
});
</script>

<style lang="scss" scoped>
.consumer-home {
  padding: 16px 24px;

  > div {
    > h3 {
      text-align: center;
      > span {
        color: #007bff;
      }
    }
  }

  > hr {
    margin: 24px 0;
    border: none;
    border-top: 1px solid #efefef;
  }

  > .main-content-box {
    > .top-banner-box {
      width: 100%;
      > .banner-container {
        width: 100%;
        aspect-ratio: 4 / 3;

        .banner-slide {
          width: 100%;
          height: 100%;
          cursor: pointer;

          > img {
            width: 100%;
            aspect-ratio: 4 / 3;
            border-radius: 8px;
            object-fit: cover;
          }
        }
      }
    }
    > nav {
      margin-top: 24px;
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 8px;
      > a {
        width: 100%;
        padding: 12px 8px;
        font-weight: 500;
        font-size: 12px;
        border-radius: 8px;
        border: 1px solid #999;
        text-align: center;
      }
    }
  }

  > .event-products-box {
    > h3 {
      margin-bottom: 24px;
    }

    > .event-products-container {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 24px;

      > button {
        border: none;
        outline: none;
        background: none;
        cursor: pointer;

        &:hover {
          > .name {
            text-decoration: underline;
          }
        }
        > img {
          width: 100%;
          aspect-ratio: 1 / 1;
          border-radius: 8px;
          object-fit: cover;
          border: 1.5px solid #efefef;
          padding: 4px;
        }

        > p {
          text-align: center;
        }

        > .name {
          font-size: 14px;
          color: #666;
          margin-top: 8px;

          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          word-break: keep-all;

          line-height: 1.5;
          height: 42px;
        }

        > .price {
          font-weight: 800;
          margin-top: 4px;
          font-size: 14px;
        }
      }
    }
  }

  > .popular-products-box {
    > h3 {
      margin-bottom: 24px;
    }

    > .products-container {
      border-top: 2px solid black;
      > .title-container {
        margin-top: 24px;
        text-align: center;
      }
      > hr {
        margin-top: 24px;
        border: none;
        border-top: 1px solid #efefef;
      }
      > .grid-container {
        flex: 1;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        padding: 24px;
        gap: 24px;

        > button {
          border: none;
          outline: none;
          background: none;
          cursor: pointer;

          &:hover {
            > .name {
              text-decoration: underline;
            }
          }
          > img {
            width: 100%;
            aspect-ratio: 1 / 1;
            border-radius: 8px;
            object-fit: cover;
            border: 1.5px solid #efefef;
            padding: 4px;
          }

          > p {
            text-align: center;
          }

          > .name {
            font-size: 14px;
            color: #666;
            margin-top: 8px;

            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            text-overflow: ellipsis;
            word-break: keep-all;

            line-height: 1.5;
            height: 42px;
          }

          > .price {
            font-weight: 800;
            margin-top: 4px;
            font-size: 14px;
          }
        }
      }

      > .empty-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-direction: column;
        gap: 16px;
        padding: 24px;
        border-bottom: 1px solid #efefef;

        > span {
          font-size: 96px;
          color: #999;
        }

        > p {
          color: #999;
          font-size: 18px;
          font-weight: 700;
        }
      }
    }
  }
}
</style>
